'use strict';

/* ============================================================
 *  Available Service – optimised build
 *  - Event delegation (fewer listeners, works with dynamic DOM)
 *  - No duplicate DOM work
 *  - for-loops instead of forEach on hot paths
 *  - Synchronous filter/apply for instant visual feedback
 * ============================================================ */

// ─── Shared utilities ────────────────────────────────────────

/** Set one toggle active, clear the rest */
function setActiveToggle(container, selector, target) {
  var items = container.querySelectorAll(selector);
  for (var i = 0; i < items.length; i++) items[i].classList.remove('is-active');
  if (target) target.classList.add('is-active');
}

/** Return unique values of a property from a list of plain objects */
function getDistinctValues(list, key) {
  var seen = {};
  var out = [];
  for (var i = 0; i < list.length; i++) {
    var v = list[i][key];
    if (v && !seen[v]) {
      seen[v] = true;
      out.push(v);
    }
  }
  return out;
}

/** Always ensure 'Other' exists in breed dropdown */
function ensureOtherBreed(values) {
  var out = values.slice();
  var hasOther = false;

  for (var i = 0; i < out.length; i++) {
    if ((out[i] || '').trim().toLowerCase() === 'other') {
      out[i] = 'Other';
      hasOther = true;
    }
  }

  if (!hasOther) out.push('Other');
  return out;
}

/** Normalise species values from the backend (Canine/Feline → Dog/Cat). */
function normalizeSpeciesName(value) {
  var v = (value || '').trim();
  var key = v.toLowerCase();
  if (key === 'dog' || key === 'dogs' || key === 'canine' || key === 'canines') return 'Dog';
  if (key === 'cat' || key === 'cats' || key === 'feline' || key === 'felines') return 'Cat';
  return v;
}

/** Keep Dog before Cat, then sort any other species alphabetically. */
function sortSpeciesValues(values) {
  var order = { Dog: 1, Cat: 2 };
  return values.sort(function (a, b) {
    var oa = order[a] || 99;
    var ob = order[b] || 99;
    if (oa !== ob) return oa - ob;
    return String(a).localeCompare(String(b));
  });
}

// ─── Dropdown animation ──────────────────────────────────────
// Handled entirely via CSS transitions on the .is-open class.
// Uses GPU-composited opacity + transform — no JS height hacks needed.

// ─── Popup / Drawer helpers ──────────────────────────────────

function openPopup(popup) {
  if (!popup) return;
  if (popup.parentNode !== document.body) document.body.appendChild(popup);
  popup.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
}

function closePopup(popup) {
  if (!popup) return;
  popup.classList.remove('is-open');
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
}

function openDrawer(drawer) {
  if (!drawer) return;
  if (drawer.parentNode !== document.body) document.body.appendChild(drawer);
  ensureBookingDrawerReady(drawer);
  drawer.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  var evt = new CustomEvent('drawer-opened', { detail: { drawer: drawer }, bubbles: true });
  drawer.dispatchEvent(evt);
}

function closeDrawer(drawer) {
  if (!drawer) return;
  drawer.classList.remove('is-open');
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  // Remove the shared drawer from the DOM after the slide-out animation finishes
  if (drawer.id === 'zigly-shared-booking-drawer') {
    setTimeout(function() {
      if (!drawer.classList.contains('is-open')) drawer.remove();
    }, 350);
  }
}

/**
 * Inject the shared booking drawer for the given card and open it.
 * Clones the <template id="zigly-booking-drawer-template">, fills product-specific
 * data from card data-* attributes, appends to body, inits, and opens.
 */
function openSharedDrawer(card) {
  // Remove any previously injected shared drawer
  var existing = document.getElementById('zigly-shared-booking-drawer');
  if (existing) existing.remove();

  var tmpl = document.getElementById('zigly-booking-drawer-template');
  if (!tmpl) return;

  var clone = tmpl.content.cloneNode(true);
  var drawer = clone.querySelector('.available-service-drawer');
  if (!drawer) return;

  // Fill product-specific data from card attributes
  var productId    = card.dataset.productId    || '';
  var variantId    = card.dataset.variantId    || '';
  var productTitle = card.dataset.productTitle || '';
  var serviceType  = card.dataset.serviceType  || '';
  var reasonForVisit = card.dataset.reasonForVisit || '';
  var serviceDuration = card.dataset.serviceDuration || '';
  // The card's service tab and species are the journey's choices for the drawer to prefill.
  var slide = card.closest('[data-role="product-slide"]');
  var serviceName = slide ? slide.dataset.serviceName || '' : '';
  var packageSpecies = slide ? slide.dataset.species || '' : '';

  drawer.dataset.productId = productId;
  // Which card opened the drawer, so the login flow can reopen this same package.
  drawer.dataset.variantId = variantId;

  var form = drawer.querySelector('.available-service-drawer__form');
  if (form) {
    form.dataset.productId      = productId;
    form.dataset.variantId      = variantId;
    form.dataset.productTitle   = productTitle;
    form.dataset.serviceType    = serviceType;
    form.dataset.reasonForVisit = reasonForVisit;
    form.dataset.serviceDuration = serviceDuration;
    form.dataset.serviceName    = serviceName;
    form.dataset.packageSpecies = packageSpecies;
  }

  // Pre-fill reason for visit input so initBookingDrawer reads the right value
  var reasonInput = drawer.querySelector('#booking-reason-shared');
  if (reasonInput) reasonInput.value = reasonForVisit;

  // Attach customer phone from the global config (set by the section Liquid script)
  var config = window.ZIGLY_BOOKING_CONFIG || {};
  if (config.customerPhone) drawer.dataset.customerPhone = String(config.customerPhone);

  document.body.appendChild(drawer);

  // Initialise event listeners, then open (which fires the drawer-opened event → API calls)
  if (typeof initBookingDrawer === 'function') {
    initBookingDrawer(drawer, {
      productId:      productId,
      variantId:      variantId,
      productTitle:   productTitle,
      serviceType:    serviceType,
      reasonForVisit: reasonForVisit
    });
  }

  openDrawer(drawer);
}

function getBookingProductDataFromDrawer(drawer) {
  if (!drawer) return null;
  var form = drawer.querySelector('.available-service-drawer__form');
  if (!form || !form.dataset.productId) return null;

  return {
    productId: form.dataset.productId,
    variantId: form.dataset.variantId || '',
    productTitle: form.dataset.productTitle || '',
    serviceType: form.dataset.serviceType || '',
    reasonForVisit: form.dataset.reasonForVisit || ''
  };
}

function ensureBookingDrawerReady(drawer) {
  if (!drawer || drawer.dataset.bookingDrawerBound === 'true') return;

  var productData = getBookingProductDataFromDrawer(drawer);
  if (productData && typeof window.initBookingDrawer === 'function') {
    window.initBookingDrawer(drawer, productData);
  } else if (typeof window.initAllBookingDrawers === 'function') {
    window.initAllBookingDrawers();
  }
}

// ═════════════════════════════════════════════════════════════
//  Per-section initialisation (filters, carousel, dropdowns)
// ═════════════════════════════════════════════════════════════

function initSection(sectionEl) {
  if (!sectionEl) return;

var carouselEl = sectionEl.querySelector('[data-role="product-carousel"]');
var skeletonLoader = sectionEl.querySelector('[data-role="skeleton-loader"]');
var togglesContainer = sectionEl.querySelector('[data-role="service-toggles"]');

var speciesSelect = sectionEl.querySelector('[data-role="species-select"]');
var breedSelect = sectionEl.querySelector('[data-role="breed-select"]');
var ageSelect = sectionEl.querySelector('[data-role="age-select"]');
var emptyState = sectionEl.querySelector('[data-role="no-results"]');

var speciesOptions = sectionEl.querySelectorAll(
  '[data-role="species-option"]'
);

if (!carouselEl || !speciesSelect || !breedSelect || !ageSelect) return;
function syncSpeciesButtons() {
  var selected = speciesSelect.value || '';

  for (var i = 0; i < speciesOptions.length; i++) {
    var option = speciesOptions[i];

    if (option.dataset.species === selected) {
      option.classList.add('is-active');
    } else {
      option.classList.remove('is-active');
    }
  }
}

for (var so = 0; so < speciesOptions.length; so++) {
  speciesOptions[so].addEventListener('click', function () {

    var selectedSpecies = this.dataset.species || '';

    // Update hidden select used by existing filtering system
    speciesSelect.value = selectedSpecies;

    // Update button UI
    syncSpeciesButtons();

    // Run existing filter system
    onFilterChange();
  });
}
  var slides = Array.prototype.slice.call(
    carouselEl.querySelectorAll('[data-role="product-slide"]')
  );
  if (!slides.length) return;

  // Pre-extract slide data into plain JS objects at init.
  // This avoids expensive DOM .dataset reads on every filter interaction.
  var slideData = new Array(slides.length);
  for (var si = 0; si < slides.length; si++) {
    var ds = slides[si].dataset;

    function clean(v) {
      return (v || '').trim();
    }

    function normalizeBreed(v) {
      var val = clean(v);
      if (!val) return '';
      return val.toLowerCase() === 'other' ? 'Other' : val;
    }

    slideData[si] = {
      serviceName: clean(ds.serviceName),
      species: normalizeSpeciesName(ds.species),
      breed: normalizeBreed(ds.breed),
      age: clean(ds.age),
      productId: clean(ds.productId)
    };
  }

  // ── Determine initial service from sessionStorage or DOM ──

  var currentService = '';
  var TOGGLE_SEL = '.available-service-section__toggle';

  if (togglesContainer) {
    try {
      var stored = sessionStorage.getItem('serviceType');
      if (stored) {
        var norm = stored.toLowerCase().trim();
        var btns = togglesContainer.querySelectorAll(TOGGLE_SEL);
        for (var i = 0; i < btns.length; i++) {
          var name = (btns[i].dataset.serviceName || '').toLowerCase().trim();
          var firstWord = name.split(' ')[0];
          if (name.includes(norm) || norm.includes(firstWord)) {
            currentService = btns[i].dataset.serviceName;
            setActiveToggle(togglesContainer, TOGGLE_SEL, btns[i]);
            break;
          }
        }
      }
    } catch (_) {}
  }

  if (!currentService && togglesContainer) {
    var active = togglesContainer.querySelector(TOGGLE_SEL + '.is-active');
    if (active) currentService = active.dataset.serviceName || '';
  }

  if (!currentService) {
    var names = getDistinctValues(slideData, 'serviceName');
    if (names.length) {
      currentService = names[0];
      if (togglesContainer) {
        var firstBtn = togglesContainer.querySelector('[data-service-name="' + currentService + '"]');
        if (firstBtn) setActiveToggle(togglesContainer, TOGGLE_SEL, firstBtn);
      }
    }
  }

  // ── Toggle button clicks ──

  if (togglesContainer) {
    var toggleButtons = togglesContainer.querySelectorAll(TOGGLE_SEL);
    toggleButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (currentService === btn.dataset.serviceName) return;

        setActiveToggle(togglesContainer, TOGGLE_SEL, btn);
        currentService = (btn.dataset.serviceName || '').trim();

        // Reset filters on tab switch
        speciesSelect.value = '';
        breedSelect.value = '';
        ageSelect.value = '';

        // Dog will auto-select in rebuildFilters if available
        rebuildFilters();
        applyFilters();
      });
    });
  }

  // ── Helper: build native <select> options ──

  function buildNativeOptions(selectEl, values, placeholder, preserveValue) {
    var frag = document.createDocumentFragment();
    var ph = document.createElement('option');
    ph.value = '';
    ph.textContent = placeholder;
    frag.appendChild(ph);

    for (var i = 0; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.textContent = values[i];
      if (values[i] === preserveValue) opt.selected = true;
      frag.appendChild(opt);
    }

    selectEl.innerHTML = '';
    selectEl.appendChild(frag);
  }

  // ── Helper: sync trigger text of a custom-select wrapper ──

  function updateTriggerText(selectEl, placeholder) {
    var wrapper = selectEl.closest('.available-service-section__custom-select');
    if (!wrapper) return;
    var txt = wrapper.querySelector('.available-service-section__select-text');
    if (!txt) return;

    if (selectEl.value) {
      var sel = selectEl.options[selectEl.selectedIndex];
      txt.textContent = sel ? sel.textContent : placeholder;
      txt.classList.add('has-value');
    } else {
      txt.textContent = placeholder;
      txt.classList.remove('has-value');
    }
  }

  // ── Custom dropdown cache ──

  var dropdownCache = new WeakMap();

  function getCached(customSelect) {
    var c = dropdownCache.get(customSelect);
    if (c) return c;
    c = {
      trigger: customSelect.querySelector('[data-role="select-trigger"]'),
      triggerText: customSelect.querySelector('.available-service-section__select-text'),
      dropdown: customSelect.querySelector('[data-role="select-dropdown"]'),
      list: customSelect.querySelector('[data-role="select-list"]'),
      searchContainer: customSelect.querySelector('[data-role="select-search"]'),
      searchInput: customSelect.querySelector('[data-role="breed-search-input"]')
    };
    dropdownCache.set(customSelect, c);
    return c;
  }

  // ── Rebuild the <li> list for one custom dropdown ──

  function rebuildCustomDropdown(selectEl) {
    var customSelect = selectEl.closest('.available-service-section__custom-select');
    if (!customSelect || customSelect.closest('.available-service-drawer')) return;

    var c = getCached(customSelect);
    if (!c.trigger || !c.triggerText || !c.dropdown || !c.list) return;

    var isBreed = selectEl.dataset.role === 'breed-select';
    if (c.searchContainer) c.searchContainer.style.display = isBreed ? 'block' : 'none';

    var options = selectEl.options;
    var selectedValue = selectEl.value;
    var allItems = [];
    var frag = document.createDocumentFragment();

    for (var i = 0; i < options.length; i++) {
      if (!options[i].value) continue;
      var li = document.createElement('li');
      li.className = 'available-service-section__select-item';
      li.textContent = options[i].textContent;
      li.dataset.value = options[i].value;
      li.dataset.searchText = options[i].textContent.toLowerCase();
      if (options[i].value === selectedValue) li.classList.add('is-selected');
      frag.appendChild(li);
      allItems.push(li);
    }

    c.list.innerHTML = '';
    c.list.appendChild(frag);

    c.list.onclick = function (e) {
      var li = e.target.closest('.available-service-section__select-item');
      if (!li) return;

      var val = li.dataset.value;
      selectEl.value = val;
      c.triggerText.textContent = li.textContent;
      c.triggerText.classList.toggle('has-value', !!val);

      if (c.searchInput) {
        c.searchInput.value = '';
        for (var j = 0; j < allItems.length; j++) allItems[j].style.display = '';
      }

      for (var k = 0; k < allItems.length; k++) allItems[k].classList.remove('is-selected');
      li.classList.add('is-selected');

      customSelect.classList.remove('is-open');

      selectEl.dispatchEvent(new Event('change', { bubbles: true }));
    };

    if (isBreed && c.searchInput) {
      c.searchInput._allListItems = allItems;
      if (!c.searchInput._listenerReady) {
        c.searchInput.addEventListener('click', function (e) {
          e.stopPropagation();
        });

        var timer;
        c.searchInput.addEventListener('input', function (e) {
          e.stopPropagation();
          clearTimeout(timer);
          timer = setTimeout(function () {
            var term = e.target.value.toLowerCase().trim();
            var items = e.target._allListItems || [];
            for (var n = 0; n < items.length; n++) {
              items[n].style.display = items[n].dataset.searchText.includes(term) ? '' : 'none';
            }
          }, 150);
        });

        c.searchInput._listenerReady = true;
      }
    }
  }

  // ── rebuildFilters – cascading Species → Breed → Age ──

  function rebuildFilters() {
    var selectedSpecies = speciesSelect.value || '';
    var selectedBreed = breedSelect.value || '';
    var selectedAge = ageSelect.value || '';

    var speciesSet = {};
    var breedSet = {};
    var ageSet = {};

    // collect species for current service
    for (var i = 0; i < slideData.length; i++) {
      var s = slideData[i];
      if (s.serviceName !== currentService) continue;
      if (s.species) speciesSet[s.species] = true;
    }

    var species = sortSpeciesValues(Object.keys(speciesSet));

    // If the current selection no longer exists for this service tab, clear it.
    if (selectedSpecies && !speciesSet[selectedSpecies]) {
      selectedSpecies = '';
      speciesSelect.value = '';
      selectedBreed = '';
      breedSelect.value = '';
      selectedAge = '';
      ageSelect.value = '';
    }

    // Default Dog on load and on tab switch if Dog exists
    if (!selectedSpecies && speciesSet['Dog']) {
      selectedSpecies = 'Dog';
      speciesSelect.value = 'Dog';
    }

    // collect breeds and ages according to selected species
    for (var j = 0; j < slideData.length; j++) {
      var s2 = slideData[j];
      if (s2.serviceName !== currentService) continue;
      if (selectedSpecies && s2.species !== selectedSpecies) continue;

      if (s2.breed) breedSet[s2.breed] = true;

      // If breed selected is Other, age should come from all packages of that species
      if (
        !selectedBreed ||
        selectedBreed === 'Other' ||
        s2.breed === selectedBreed
      ) {
        if (s2.age) ageSet[s2.age] = true;
      }
    }

    var breeds = Object.keys(breedSet);
    breeds = ensureOtherBreed(breeds);

    buildNativeOptions(speciesSelect, species, 'Select Species', selectedSpecies);
    buildNativeOptions(breedSelect, breeds, 'Select Breed', selectedBreed);

    // reset breed if no longer valid
    if (selectedBreed && breeds.indexOf(selectedBreed) === -1) {
      breedSelect.value = '';
      selectedBreed = '';
      ageSelect.value = '';

      ageSet = {};
      for (var k = 0; k < slideData.length; k++) {
        var s3 = slideData[k];
        if (s3.serviceName !== currentService) continue;
        if (selectedSpecies && s3.species !== selectedSpecies) continue;
        if (s3.age) ageSet[s3.age] = true;
      }
    }

    var ages = Object.keys(ageSet);
    buildNativeOptions(ageSelect, ages, 'Select Age group', selectedAge);

    if (selectedAge && !ageSet[selectedAge]) {
      ageSelect.value = '';
    }

    rebuildCustomDropdown(speciesSelect);
    rebuildCustomDropdown(breedSelect);
    rebuildCustomDropdown(ageSelect);

updateTriggerText(speciesSelect, 'Select Species');
updateTriggerText(breedSelect, 'Select Breed');
updateTriggerText(ageSelect, 'Select Age group');

syncSpeciesButtons();
  }

  // ── Dropdown open/close wiring ──

  function initCustomDropdowns() {
    var customSelects = Array.prototype.slice.call(
      sectionEl.querySelectorAll('.available-service-section__custom-select')
    ).filter(function (cs) {
      return !cs.closest('.available-service-drawer');
    });

    customSelects.forEach(function (cs) {
      var c = getCached(cs);
      var selectEl = cs.querySelector('select');
      if (!c.trigger || !selectEl || !c.dropdown) return;

      c.trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = cs.classList.contains('is-open');

        for (var i = 0; i < customSelects.length; i++) {
          if (customSelects[i] !== cs && customSelects[i].classList.contains('is-open')) {
            customSelects[i].classList.remove('is-open');
          }
        }

        if (!isOpen) {
          cs.classList.add('is-open');
          if (selectEl.dataset.role === 'breed-select' && c.searchInput) {
            c.searchInput.value = '';
            var items = c.list.children;
            for (var j = 0; j < items.length; j++) items[j].style.display = '';
          }
        } else {
          cs.classList.remove('is-open');
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (e.target.closest('.available-service-drawer')) return;
      for (var i = 0; i < customSelects.length; i++) {
        if (!customSelects[i].contains(e.target) && customSelects[i].classList.contains('is-open')) {
          customSelects[i].classList.remove('is-open');
        }
      }
    });
  }

  // ── Apply slide visibility filters ──

  function applyFilters() {
    var sp = speciesSelect.value || '';
    var br = breedSelect.value || '';
    var ag = ageSelect.value || '';
    var seen = {};
    var visible = 0;

    for (var i = 0; i < slideData.length; i++) {
      var s = slideData[i];
      var id = s.productId;

      var breedMatches = false;

      if (!br) {
        breedMatches = true;
      } else if (br === 'Other') {
        // "Other" should show all packages of the selected species
        breedMatches = true;
      } else {
        breedMatches = s.breed === br;
      }

      var show = s.serviceName === currentService &&
        (!sp || s.species === sp) &&
        breedMatches &&
        (!ag || s.age === ag) &&
        id && !seen[id];

      if (show) {
        seen[id] = true;
        slides[i].classList.remove('hidden');
        visible++;
      } else {
        slides[i].classList.add('hidden');
      }
    }

    if (emptyState) emptyState.classList.toggle('hidden', visible > 0);
    carouselEl.classList.toggle('single-card', visible === 1);

    if (carouselEl._availableServiceSwiper) {
      requestAnimationFrame(function () {
        carouselEl._availableServiceSwiper.update();
        carouselEl._availableServiceSwiper.slideTo(0, 0);
      });
    }
  }

  // ── Filter change events ──

  function onFilterChange() {
    rebuildFilters();
    applyFilters();
  }

  speciesSelect.addEventListener('change', onFilterChange);
  breedSelect.addEventListener('change', onFilterChange);
  ageSelect.addEventListener('change', onFilterChange);

  // ── Swiper ──

  function initSwiper() {
    if (carouselEl._availableServiceSwiper || typeof Swiper !== 'function') return;
    carouselEl._availableServiceSwiper = new Swiper(carouselEl, {
      slidesPerView: 1.2,
      breakpoints: {
        750: { slidesPerView: 2.2 },
        1024: { slidesPerView: 3.5 },
        1200: { slidesPerView: 4.2 }
      },
      navigation: {
        nextEl: sectionEl.querySelector('.swiper-button-next'),
        prevEl: sectionEl.querySelector('.swiper-button-prev')
      },
      pagination: {
        el: sectionEl.querySelector('[data-role="carousel-pagination"]'),
        clickable: true
      },
      lazy: { loadPrevNext: true, loadPrevNextAmount: 2 }
    });
  }

  function startSwiper() {
    if ('IntersectionObserver' in window) {
      var obs = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          initSwiper();
          obs.disconnect();
        }
      }, { rootMargin: '50px' });
      obs.observe(carouselEl);
    } else {
      initSwiper();
    }
  }

  // Initial build
  rebuildFilters();
  updateTriggerText(speciesSelect, 'Select Species');

  requestAnimationFrame(function () {
    applyFilters();
  });

  initCustomDropdowns();

  if (skeletonLoader) skeletonLoader.classList.add('hidden');
  carouselEl.classList.remove('hidden');

  requestAnimationFrame(startSwiper);
}

// ═════════════════════════════════════════════════════════════
//  Boot all sections + optional scroll-to-section
// ═════════════════════════════════════════════════════════════

function initAllSections() {
  var sections = document.querySelectorAll('[data-available-service-section]');

  var idx = 0;
  function next() {
    if (idx < sections.length) {
      var sectionEl = sections[idx++];
      if (sectionEl && !sectionEl.hasAttribute('data-lazy-load')) {
        initSection(sectionEl);
      }
      if (idx < sections.length) requestAnimationFrame(next);
    }
  }
  next();

  try {
    if (sessionStorage.getItem('scrollToAvailableService') !== 'true' || !sections.length) return;
    sessionStorage.removeItem('scrollToAvailableService');

    var target = sections[0];
    var headerOffset = 80;
    var attempts = 0;

    function doScroll() {
      var rect = target.getBoundingClientRect();
      if (rect.height === 0 && ++attempts < 10) return setTimeout(doScroll, 100);
      window.scrollTo({
        top: Math.max(0, rect.top + window.pageYOffset - headerOffset),
        behavior: 'smooth'
      });
    }

    setTimeout(doScroll, 50);
  } catch (_) {}
}

// ═════════════════════════════════════════════════════════════
//  Lazy-load full section markup via Shopify Section Rendering API
// ═════════════════════════════════════════════════════════════

function lazyLoadAvailableServiceSections() {
  var placeholders = document.querySelectorAll(
    '[data-available-service-section][data-lazy-load="true"][data-section-id]'
  );
  if (!placeholders.length) return;

  placeholders.forEach(function (sectionEl) {
    var sectionId = sectionEl.getAttribute('data-section-id');
    if (!sectionId) return;

    if (sectionEl._availableServiceLazyLoaded) return;
    sectionEl._availableServiceLazyLoaded = true;

    var url = window.location.pathname +
      '?sections=' + encodeURIComponent(sectionId) +
      '&available_service_full=1';

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('Failed to fetch section HTML');
        return res.json();
      })
      .then(function (data) {
        var html = data && data[sectionId];
        if (!html) return;

        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var newSection = wrapper.querySelector('[data-available-service-section]');
        if (!newSection) return;

        newSection.setAttribute('data-section-id', sectionId);

        sectionEl.parentNode.replaceChild(newSection, sectionEl);
        initSection(newSection);

        // New drawers arrive with the lazily loaded section. Rebind booking
        // drawer listeners immediately so Book Now opens and fetches pet/clinic data.
        if (typeof window.initAllBookingDrawers === 'function') {
          window.initAllBookingDrawers();
        }

        var evt = new CustomEvent('available-service:section-loaded', {
          detail: { section: newSection },
          bubbles: true
        });
        newSection.dispatchEvent(evt);
      })
      .catch(function (err) {
        console.error('[Available Service] Lazy load failed:', err);
      });
  });
}

// ═════════════════════════════════════════════════════════════
//  Popups & Drawers – single delegated document listener
// ═════════════════════════════════════════════════════════════

// Popups ship inside a <template> per product to keep them out of the initial
// DOM. Clone a product's popups into the document the first time one is opened.
var materialisedPopupProductIds = Object.create(null);
function ensurePopupsMaterialised(productId) {
  if (!productId || materialisedPopupProductIds[productId]) return;
  materialisedPopupProductIds[productId] = true;
  var tmpl = document.querySelector(
    'template.available-service-popups[data-product-id="' + productId + '"]'
  );
  if (tmpl && tmpl.content) {
    document.body.appendChild(tmpl.content.cloneNode(true));
  }
}

function initPopupsAndDrawers() {
  document.addEventListener('click', function (e) {
    var t = e.target;

    var viewLink = t.closest('[data-action="view-details"]');
    if (viewLink) {
      e.preventDefault();
      var card = viewLink.closest('.available-service-card');
      if (card) {
        ensurePopupsMaterialised(card.dataset.productId);
        var popup = document.getElementById('popup-view-details-' + card.dataset.productId);
        if (popup) openPopup(popup);
      }
      return;
    }

    var serviceItem = t.closest('.available-service-card__included-item');
    if (serviceItem) {
      var detail = serviceItem.dataset.serviceDetail;
      var card2 = serviceItem.closest('.available-service-card');
      if (card2 && detail) {
        ensurePopupsMaterialised(card2.dataset.productId);
        var popup2 = document.getElementById('popup-service-' + detail + '-' + card2.dataset.productId);
        if (popup2) openPopup(popup2);
      }
      return;
    }

    var bookBtn = t.closest('[data-action="book-now"]');
    if (bookBtn) {
      e.preventDefault();
      e.stopPropagation();
      var card3 = bookBtn.closest('.available-service-card');
      if (card3) openSharedDrawer(card3);
      return;
    }

    if (t.closest('.available-service-popup__close')) {
      var p = t.closest('.available-service-popup');
      if (p) closePopup(p);
      return;
    }

    if (t.classList.contains('available-service-popup__overlay')) {
      var p2 = t.closest('.available-service-popup');
      if (p2) closePopup(p2);
      return;
    }

    if (t.closest('.available-service-drawer__close')) {
      var d = t.closest('.available-service-drawer');
      if (d) closeDrawer(d);
      return;
    }

    if (t.classList.contains('available-service-drawer__overlay')) {
      var d2 = t.closest('.available-service-drawer');
      if (d2) closeDrawer(d2);
      return;
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var popups = document.querySelectorAll('.available-service-popup.is-open');
    for (var i = 0; i < popups.length; i++) closePopup(popups[i]);
    var drawers = document.querySelectorAll('.available-service-drawer.is-open');
    for (var j = 0; j < drawers.length; j++) closeDrawer(drawers[j]);
  });
}

// ═════════════════════════════════════════════════════════════
//  Entry point
// ═════════════════════════════════════════════════════════════

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    initAllSections();
    lazyLoadAvailableServiceSections();
    initPopupsAndDrawers();
  });
} else {
  initAllSections();
  lazyLoadAvailableServiceSections();
  initPopupsAndDrawers();
}